import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { storyRouteLinkProps } from "./ctaTargets";
import ContentReviewChip from "../../components/ContentReviewChip";
import { COLORS } from "../../theme/tokens";

const CARD_SX = {
  backgroundColor: "#fff",
  borderRadius: 1,
  p: 3,
  height: "100%",
  border: "1px solid rgba(32, 48, 51, 0.14)",
  boxShadow: "0 16px 36px rgba(32, 48, 51, 0.08)",
};

function ContactCard({ block }) {
  const c = block.content || {};
  return (
    <Stack spacing={0.75} sx={CARD_SX}>
      {c.eyebrow && (
        <Typography
          variant="overline"
          sx={{
            color: COLORS.muted,
            letterSpacing: "0.12em",
            fontWeight: 700,
            fontSize: "0.78rem",
          }}
        >
          {c.eyebrow}
        </Typography>
      )}
      {c.heading && (
        <Typography
          variant="h5"
          component="h3"
          sx={{ fontFamily: "windroos, sans-serif", color: COLORS.blue, m: 0, mb: 1 }}
        >
          {c.heading}
        </Typography>
      )}
      <ContentReviewChip status="overgenomen" sx={{ my: 0.5 }} />
      {c.name && <Typography variant="body1" sx={{ fontWeight: 600 }}>{c.name}</Typography>}
      {c.street && <Typography variant="body2" sx={{ color: COLORS.muted }}>{c.street}</Typography>}
      {c.city && <Typography variant="body2" sx={{ color: COLORS.muted }}>{c.city}</Typography>}
      {c.phone && (
        <Typography variant="body2" sx={{ color: COLORS.muted }}>
          T:{" "}
          <a href={`tel:${c.phone}`} style={{ color: COLORS.red, textDecoration: "none", fontWeight: 600 }}>
            {c.phone}
          </a>
        </Typography>
      )}
      {c.email && (
        <Typography variant="body2" sx={{ color: COLORS.muted }}>
          E:{" "}
          <a href={`mailto:${c.email}`} style={{ color: COLORS.red, textDecoration: "none", fontWeight: 600 }}>
            {c.email}
          </a>
        </Typography>
      )}
    </Stack>
  );
}

function DocumentenCard({ block }) {
  const c = block.content || {};
  return (
    <Stack spacing={1} sx={CARD_SX}>
      {c.eyebrow && (
        <Typography
          variant="overline"
          sx={{
            color: COLORS.muted,
            letterSpacing: "0.12em",
            fontWeight: 700,
            fontSize: "0.78rem",
          }}
        >
          {c.eyebrow}
        </Typography>
      )}
      {c.heading && (
        <Typography
          variant="h5"
          component="h3"
          sx={{ fontFamily: "windroos, sans-serif", color: COLORS.blue, m: 0 }}
        >
          {c.heading === "Alles wat ouders nodig hebben"
            ? "Klaar om kennis te maken?"
            : c.heading}
        </Typography>
      )}
      <ContentReviewChip status="overgenomen" sx={{ my: 0.5 }} />
      {c.body && (
        <Typography variant="body2" sx={{ color: COLORS.muted }}>
          {c.body}
        </Typography>
      )}
      {c.ctaLabel && c.ctaRoute && (
        <Box sx={{ mt: "auto", pt: 1 }}>
          <Box
            component={Link}
            {...storyRouteLinkProps(c.ctaRoute)}
            sx={{
              color: COLORS.red,
              fontWeight: 600,
              textDecoration: "none",
              fontSize: "0.92rem",
              minHeight: 24,
              py: 0.5,
              display: "inline-flex",
              alignItems: "center",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {c.ctaLabel} →
          </Box>
        </Box>
      )}
    </Stack>
  );
}

export default function CtaBandSection({ section }) {
  const { content, blocks = [] } = section;
  const sorted = [...blocks].sort((a, b) => a.order - b.order);
  const contactCard = sorted.find((b) => b.blockType === "text-card");
  const ctaBlock = sorted.find((b) => b.blockType === "cta");

  return (
    <Box
      id="contact-aanmelden"
      component="section"
      sx={{
        py: "clamp(48px, 7vw, 92px)",
        px: "clamp(20px, 5vw, 72px)",
        backgroundColor: "#fff",
        color: "#1f3a3d",
        scrollMarginTop: { xs: 64, md: 80 },
      }}
    >
      <Container disableGutters sx={{ maxWidth: { md: 1160 } }} maxWidth={false}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
            gap: "20px",
            alignItems: "center",
            padding: "clamp(26px, 4vw, 40px)",
            borderRadius: 1,
            backgroundColor: COLORS.blue,
            color: "#fff",
            boxShadow: "0 16px 36px rgba(32, 48, 51, 0.12)",
          }}
        >
          <Box>
            {content.eyebrow && (
              <Typography
                variant="overline"
                sx={{
                  color: "#fff",
                  letterSpacing: "0.12em",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                {content.eyebrow}
              </Typography>
            )}
            {content.heading && (
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "windroos, Georgia, serif",
                  color: "#fff",
                  fontSize: { xs: "1.8rem", md: "2.4rem" },
                  lineHeight: 1.08,
                  mt: 0.5,
                  mb: content.body ? 1.25 : 0,
                }}
              >
                {content.heading}
              </Typography>
            )}
            {content.body && (
              <Typography
                variant="body1"
                sx={{ color: "#fff", opacity: 0.95, maxWidth: 640 }}
              >
                {content.body}
              </Typography>
            )}
          </Box>
          {content.ctaLabel && content.ctaRoute && (
            <Button
              component={Link}
              {...storyRouteLinkProps(content.ctaRoute)}
              variant="contained"
              size="large"
              sx={{
                m: 0,
                px: 3,
                minHeight: 48,
                fontWeight: 800,
                borderRadius: 1.5,
                textTransform: "none",
                backgroundColor: "#EF7D00",
                color: "#203033",
                boxShadow: "0 10px 24px rgba(239, 125, 0, 0.32)",
                "&:hover": { backgroundColor: "#E87800" },
                justifySelf: { md: "end" },
              }}
            >
              {content.ctaRoute === "/aanmelden" ? "Kennismaken" : content.ctaLabel}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            mt: "24px",
            display: "grid",
            gap: { xs: 2.5, md: "22px" },
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {contactCard && <ContactCard block={contactCard} />}
          {ctaBlock && <DocumentenCard block={ctaBlock} />}
        </Box>
      </Container>
    </Box>
  );
}
